import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { Frame } from '../store/types';
import type { LottieHandle, LottieJson } from './types';
import { scanRoot } from './scan';
import { mountLottie } from './runtime';
import { resolveLottieSlots } from './resolveSlots';
import { loadAssetJson } from './loadAssetJson';
import { registerLottie, unregisterLottie } from './registry';
import { useCanvasStore } from '../store/canvas-store';
import { useTheme } from '../chrome/theme';
import { errorRing } from '../agent/errorBuffer';
import { normalizeComputedColor, paintColorToRgba255 } from './resolveCssColor';

/** Probe a CSS token (e.g. var(--color-accent)) against the rendered root, then
 *  normalise it to 0–1 rgba. The computed value of an oklch/light-dark accent
 *  serialises as "oklch(…)", so we canvas-rasterise it rather than rgb-parse. */
function makeTokenResolver(root: HTMLElement) {
  return (cssValue: string): [number, number, number, number] | null => {
    const probe = document.createElement('span');
    probe.style.color = cssValue;
    root.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    probe.remove();
    return normalizeComputedColor(computed, paintColorToRgba255);
  };
}

const htmlOf = (f: Frame): string => ('html' in f ? (f as { html: string }).html : '');

/**
 * Hydrate every [data-lottie] element under the rendered frame root. Mounted in
 * the render path next to useFrameScript/useFrameMotion. No lottie elements →
 * cheap no-op. Errors (missing/unparsable asset, mount failure) go to the agent
 * errorRing instead of a silent blank.
 *
 * `renderedHtml` is the html actually written to the DOM (asset-resolved). It is
 * a dep so the hook re-hydrates whenever the frame's innerHTML is rewritten —
 * critically, when async asset resolution swaps `data-lottie="asset://…"` for a
 * blob url, `dangerouslySetInnerHTML` wipes the mounted SVG, so we must re-mount.
 */
export function useFrameLottie(ref: RefObject<HTMLElement>, frame: Frame, renderedHtml?: string): void {
  const activeThemeId = useCanvasStore((s) => s.doc.activeThemeId);
  const themeMode = useTheme((s) => s.theme);
  const html = htmlOf(frame);

  useEffect(() => {
    const root = ref.current;
    if (!root || !html.includes('data-lottie')) return;
    let disposed = false;
    const handles: LottieHandle[] = [];

    void (async () => {
      const resolveToken = makeTokenResolver(root);
      for (const spec of scanRoot(root)) {
        const el =
          root.querySelector<HTMLElement>(`[data-cf-id="${spec.cfNodeId}"]`) ??
          root.querySelector<HTMLElement>(`[data-lottie="${spec.ref}"]`);
        if (!el) continue;
        const json = (await loadAssetJson(spec.ref)) as LottieJson | null;
        if (disposed) break;
        if (!json) {
          errorRing.push(`lottie[${frame.id}]: asset ${spec.ref} missing or unparsable`);
          continue;
        }
        el.replaceChildren(); // hydration owns the element's children
        try {
          const resolved = resolveLottieSlots(json, spec.slots, resolveToken);
          const h = await mountLottie(el, resolved, { loop: spec.loop, autoplay: spec.autoplay, speed: spec.speed });
          if (disposed) {
            h.destroy(); // cleanup fired while mount was pending — don't leak the orphan
            break;
          }
          handles.push(h);
        } catch (err) {
          errorRing.push(`lottie[${frame.id}]: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
      if (!disposed) registerLottie(frame.id, handles);
    })();

    return () => {
      disposed = true;
      for (const h of handles) h.destroy();
      unregisterLottie(frame.id);
    };
  }, [ref, frame.id, html, renderedHtml, activeThemeId, themeMode]);
}
